import TrackProductEvents from '@/graphql/mutations/TrackProductEvents'
import cognitoAuth from '@/cognito'
import { getTourContext, onTourChange } from '@/utils/helpHero'
import { ACTIVITY_PING, MAX_EVENTS_PER_CALL } from '@/shared/productEvents/recording'

const FLUSH_INTERVAL_MS = 5000
const ACTIVITY_PING_INTERVAL_MS = 5 * 60 * 1000
const ACTIVITY_EVENTS = ['pointerdown', 'keydown']
const TOKEN_TIMEOUT_MS = 10000
const TOKEN_EXPIRY_MARGIN_MS = 60 * 1000

const queue = []
let flushTimerId = null
let lastActivityPingAt = 0
let lastKnownToken = null
let lastKnownTokenExpiresAt = 0
let projectIdOf = () => undefined

const isEnabled = () => typeof window !== 'undefined' &&
  !window.Cypress &&
  Boolean(process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT)

const requestBody = (batch) => JSON.stringify({
  operationName: 'trackProductEvents',
  query: TrackProductEvents,
  variables: { events: JSON.stringify(batch) }
})

// sentAt lets the server measure how long ago each event happened by the device's own clock,
// so a device clock that is off by minutes does not move events in time.
const send = async (batch, token) => {
  const sentAt = new Date().toISOString()

  await fetch(process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: requestBody(batch.map((event) => ({ ...event, sentAt }))),
    keepalive: true
  })
}

// Only for the cached token at page hide, when there is no time to ask Cognito. Its expiry comes
// from Cognito already corrected for this device's clock drift.
const isStillUsable = (token) => Boolean(token) && Date.now() < lastKnownTokenExpiresAt - TOKEN_EXPIRY_MARGIN_MS

const currentToken = async () => {
  try {
    const timeout = new Promise((resolve) => setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS))
    const session = await Promise.race([cognitoAuth.getIdTokenWithExpiryPromise(), timeout])

    if (session?.token) {
      lastKnownToken = session.token
      lastKnownTokenExpiresAt = session.expiresAt

      return session.token
    }
  } catch (err) {
    // Signed out or the refresh failed: nobody to attribute the batch to.
  }

  return null
}

export const flushProductEvents = async () => {
  if (queue.length === 0) {
    return
  }

  const batch = queue.splice(0, MAX_EVENTS_PER_CALL)
  const token = await currentToken()

  if (token) {
    await send(batch, token).catch(() => {})
  } else {
    // Dropped before login: let the next interaction ping again instead of waiting out the interval.
    lastActivityPingAt = 0
  }

  if (queue.length > 0) {
    scheduleFlush()
  }
}

// The page may be gone before a token refresh resolves, so only a cached token that is still valid is used here.
const flushOnPageHide = () => {
  if (queue.length === 0 || !isStillUsable(lastKnownToken)) {
    return
  }

  send(queue.splice(0, MAX_EVENTS_PER_CALL), lastKnownToken).catch(() => {})
}

const scheduleFlush = () => {
  if (flushTimerId) {
    return
  }

  flushTimerId = setTimeout(() => {
    flushTimerId = null
    flushProductEvents()
  }, FLUSH_INTERVAL_MS)
}

// On logout: nothing queued or cached may be sent as the previous user once someone else signs in.
export const resetProductEvents = () => {
  queue.length = 0
  lastKnownToken = null
  lastKnownTokenExpiresAt = 0
  // The next user's first interaction must ping, or a short visit right after the switch has no session.
  lastActivityPingAt = 0
  if (flushTimerId) {
    clearTimeout(flushTimerId)
    flushTimerId = null
  }
}

export const trackProductEvent = (eventName, properties = {}) => {
  if (!isEnabled()) {
    return
  }

  queue.push({
    eventName,
    clientAt: new Date().toISOString(),
    ...properties,
    projectId: properties.projectId ?? projectIdOf(),
    ...getTourContext()
  })
  scheduleFlush()
}

// The server starts a new session after 30 idle minutes, so reading or scrolling must keep it alive too.
const markActivity = () => {
  const now = Date.now()

  if (now - lastActivityPingAt < ACTIVITY_PING_INTERVAL_MS) {
    return
  }

  lastActivityPingAt = now
  trackProductEvent(ACTIVITY_PING)
}

const recordTourChange = ({ started, workflowId, outcome }) => {
  trackProductEvent(started ? 'tour_started' : 'tour_ended', { workflowId, outcome })
}

export const initProductEvents = ({ store }) => {
  if (!isEnabled()) {
    return
  }

  projectIdOf = () => store.state.projectMemberships.activeProjectId
  store.subscribe((mutation) => {
    if (mutation.type === 'logout') {
      resetProductEvents()
    }
  })
  ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, markActivity, { passive: true }))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      markActivity()
    } else {
      flushOnPageHide()
    }
  })
  window.addEventListener('pagehide', flushOnPageHide)
  onTourChange(recordTourChange)
  markActivity()
}
